import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Play, Search, List, Grid2X2, Star, RefreshCw, Layers, Film, X } from 'lucide-react';
import { loadUnifiedAnime, unifiedEpisodeRoute, type UnifiedAnimeSnapshot, type EpisodeVariant, type UnifiedEpisode } from '../../anime/UnifiedAnime.ts';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import type { HomeMedia } from '../../metadata/HomeRecommendations.ts';
import { EpisodeDeliveryBadge } from './EpisodeDeliveryBadge.tsx';

export function UnifiedAnimeEpisodes({
  media,
  onLink,
  startRequested = 0,
  loadEpisodes = loadUnifiedAnime,
  onSnapshot,
}: {
  media: HomeMedia;
  onLink: () => void;
  startRequested?: number;
  loadEpisodes?: typeof loadUnifiedAnime;
  onSnapshot?: (state: UnifiedAnimeSnapshot) => void;
}) {
  const handledStart = useRef(0);
  const [snapshot, setSnapshot] = useState<UnifiedAnimeSnapshot>({ installed: 0, completed: 0, episodes: [], issues: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [filter, setFilter] = useState('');
  const [grid, setGrid] = useState(true); // Default to modern Grid distribution!
  const [selected, setSelected] = useState<UnifiedEpisode | null>(null);
  const [favorite, setFavorite] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setFavorite('');
    setSelected(null);
    setSnapshot({ installed: 0, completed: 0, episodes: [], issues: [] });

    void yomiriDatabase.getSetting<string>(`anime-preferred-source:${media.id}`, '')
      .then((value) => {
        if (!controller.signal.aborted) setFavorite(value || '');
      })
      .catch(() => {
        if (!controller.signal.aborted) setError('No se pudo leer la fuente favorita.');
      });

    void loadEpisodes(
      media,
      (state) => {
        setSnapshot(state);
        onSnapshot?.(state);
      },
      controller.signal
    )
      .catch((cause) => {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'No se pudieron cargar los episodios.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    const changed = () => setRevision((value) => value + 1);
    window.addEventListener('yomiri-sources-changed', changed);
    return () => {
      controller.abort();
      window.removeEventListener('yomiri-sources-changed', changed);
    };
  }, [media.id, revision]);

  const open = (episode: UnifiedEpisode) => {
    const preferred = episode.variants.find((item) => item.sourceId === favorite);
    if (preferred) unifiedEpisodeRoute(preferred, media.id);
    else setSelected(episode);
  };

  useEffect(() => {
    if (!loading && startRequested > handledStart.current) {
      handledStart.current = startRequested;
      if (snapshot.episodes[0]) open(snapshot.episodes[0]);
      else onLink();
    }
  }, [startRequested, loading, snapshot.episodes, favorite]);

  const saveFavorite = async (variant: EpisodeVariant) => {
    const value = favorite === variant.sourceId ? '' : variant.sourceId;
    try {
      await yomiriDatabase.setSetting(`anime-preferred-source:${media.id}`, value);
      setFavorite(value);
    } catch {
      setError('No se pudo guardar la fuente favorita.');
    }
  };

  const filtered = snapshot.episodes.filter((row) =>
    `${row.number} ${row.name}`.toLowerCase().includes(filter.trim().toLowerCase())
  );

  return (
    <section className="nyomiri-unified-episodes">
      {/* Header with Title & Count Pill */}
      <div className="nyomiri-episodes-header">
        <div className="nyomiri-episodes-heading">
          <h2>Episodios</h2>
          {snapshot.episodes.length > 0 && (
            <span className="nyomiri-episodes-count">{snapshot.episodes.length}</span>
          )}
        </div>

        {/* Toolbar: Search, View Mode Toggle, Refresh */}
        <div className="nyomiri-episode-toolbar">
          <label className="nyomiri-episode-search">
            <Search size={14} aria-hidden="true" />
            <input
              aria-label="Buscar episodios"
              placeholder="Buscar por número o título..."
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </label>
          <div className="nyomiri-view-mode-toggle" role="group" aria-label="Modo de vista">
            <button
              aria-label="Vista de cuadrícula"
              aria-pressed={grid}
              onClick={() => setGrid(true)}
              title="Cuadrícula"
            >
              <Grid2X2 size={15} />
            </button>
            <button
              aria-label="Vista de lista"
              aria-pressed={!grid}
              onClick={() => setGrid(false)}
              title="Lista"
            >
              <List size={15} />
            </button>
          </div>
          <button
            className="nyomiri-btn-icon"
            aria-label="Actualizar episodios"
            disabled={loading}
            onClick={() => setRevision((value) => value + 1)}
            title="Recargar episodios"
          >
            <RefreshCw size={15} className={loading ? 'nyomiri-spin' : ''} />
          </button>
        </div>
      </div>

      {error && <p className="nyomiri-alert-banner" role="alert">{error}</p>}

      {/* Skeletons while loading */}
      {loading && !filtered.length && (
        <div className={'nyomiri-episode-list is-loading' + (grid ? ' is-grid' : '')} aria-busy="true">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="nyomiri-episode-skeleton" />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!filtered.length && !loading && (
        <div className="nyomiri-episode-empty">
          <div className="nyomiri-empty-icon-wrap">
            <Film size={28} />
          </div>
          <strong>{filter ? 'No hay episodios que coincidan con la búsqueda' : 'Aún no hay episodios disponibles'}</strong>
          <p>
            {snapshot.installed
              ? 'No se encontraron episodios en las coincidencias confirmadas. Podés vincular una edición manualmente.'
              : 'Instalá tus fuentes desde la sección Fuentes para consultar su catálogo de video.'}
          </p>
          <button className="nyomiri-btn nyomiri-btn-neutral" onClick={onLink}>
            <Layers size={14} />
            Vincular edición o cambiar fuentes
          </button>
        </div>
      )}

      {/* Episode Cards Grid or List */}
      {filtered.length > 0 && (
        <div className={'nyomiri-episode-list' + (grid ? ' is-grid' : '')}>
          {filtered.map((row, index) => {
            const epNum = row.number != null ? (row.number < 10 ? `0${row.number}` : String(row.number)) : (index + 1 < 10 ? `0${index + 1}` : String(index + 1));
            return (
              <button
                key={row.key}
                className="nyomiri-episode-card"
                onClick={() => open(row)}
                title={`Reproducir ${row.name}`}
              >
                <div className="nyomiri-episode-card-top">
                  <span className="nyomiri-episode-pill">EP {epNum}</span>
                  <div className="nyomiri-episode-play-icon">
                    <Play size={13} fill="currentColor" />
                  </div>
                </div>
                <strong className="nyomiri-episode-card-title">{row.name}</strong>
                <div className="nyomiri-episode-card-footer">
                  <span className="nyomiri-source-pill">
                    <span className="nyomiri-source-dot" />
                    {row.variants.length} {row.variants.length === 1 ? 'fuente' : 'fuentes'}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {snapshot.issues.length > 0 && (
        <details className="nyomiri-source-issues">
          <summary>{snapshot.issues.length} fuentes sin coincidencia o sin respuesta</summary>
          <div className="nyomiri-source-issues-body">
            {snapshot.issues.map((issue) => (
              <p key={issue.sourceId}>
                <strong>{issue.sourceId}:</strong> {issue.message}
              </p>
            ))}
            <button className="nyomiri-btn nyomiri-btn-neutral" onClick={onLink}>
              Revisar coincidencias
            </button>
          </div>
        </details>
      )}

      {/* Source Selection Dialog - Compact, Fast & Clean */}
      <Dialog
        className="yomiri-panel y-ep-sources-dialog"
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: {
            className: 'y-ep-sources-paper',
          },
        }}
      >
        <DialogTitle className="y-ep-sources-title">
          <div className="y-ep-sources-header-left">
            <span className="y-ep-sources-pill">{selected?.name}</span>
            <span className="y-ep-sources-subtext">Elegí la fuente para reproducir</span>
          </div>
          <button
            type="button"
            className="y-ep-sources-close"
            onClick={() => setSelected(null)}
            aria-label="Cerrar"
          >
            <X size={15} />
          </button>
        </DialogTitle>

        <DialogContent className="y-ep-sources-body">
          <div className="y-ep-variants-grid">
            {selected?.variants.map((variant) => {
              const isFav = favorite === variant.sourceId;
              return (
                <div
                  className={`y-ep-variant-card ${isFav ? 'is-fav' : ''}`}
                  key={variant.sourceId + variant.episode.id}
                >
                  <button
                    type="button"
                    className="y-ep-variant-action"
                    onClick={() => unifiedEpisodeRoute(variant, media.id)}
                  >
                    <div className="y-ep-variant-play-btn">
                      <Play size={13} fill="currentColor" />
                    </div>
                    <div className="y-ep-variant-details">
                      <div className="y-ep-variant-name-row">
                        <span className="y-ep-variant-source">{variant.sourceName}</span>
                        {isFav && <span className="y-ep-fav-badge">Favorita</span>}
                      </div>
                      <EpisodeDeliveryBadge variant={variant} />
                    </div>
                  </button>

                  <button
                    type="button"
                    className={`y-ep-fav-toggle ${isFav ? 'is-active' : ''}`}
                    aria-label={`Fuente favorita: ${variant.sourceName}`}
                    aria-pressed={isFav}
                    onClick={() => void saveFavorite(variant)}
                    title={isFav ? 'Favorita (clic para desmarcar)' : 'Marcar como favorita'}
                  >
                    <Star
                      size={16}
                      fill={isFav ? '#ff2e63' : 'none'}
                      color={isFav ? '#ff2e63' : 'rgba(255, 255, 255, 0.35)'}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
